import RenderQuality from "../../render-quality";
import PerfTestMaterial from "../../rendering/material-perftest";
import getKnownGPUS from "./known-gpus";

const TIME_UNTIL_TIMEOUT_MS = 5000;         // time until test timeouts and defaults to low quality
const MAX_ALLOWED_TIME_PER_FRAME_MS = 30;   // max allowed average time per frame until low quality
const GPU_GRACE_PERIOD = 10;                // run grace period frames to account for gpu initialization
const GPU_TEST_ITERATIONS = 100;            // how many iterations to run gpu tests for
const SHADER_ITERATION_COUNT = 1000;        // how many shader iterations to run, increase this for shader complexity
const ALWAYS_CHECK_GPU = false;             // should the known gpu list be checked before gpu test is run

async function checkGPU() {
    let quality = RenderQuality.NORMAL;

    const knownGPUS = await getKnownGPUS();
    const listGPUs = Object.keys(knownGPUS);
    const renderer = new THREE.WebGLRenderer();
    const gl = renderer.context;
    const nameGPU = getUnmaskedInfo(gl).renderer;
    const foundGPU = listGPUs.find(gpu => nameGPU.includes(gpu));

    renderer.context.canvas.addEventListener("webglcontextlost", function(event) {
        console.error(event);
    }, false);

    if (ALWAYS_CHECK_GPU || !foundGPU) {
        try {
            const KEY_PERF_TEST = "gpuPerformance";
            const cache = await caches.open(KEY_PERF_TEST);
            const prevPerformance = ALWAYS_CHECK_GPU ? null : await cache.match("/score");
            const gpuPerformance = prevPerformance
                ? parseFloat(await (prevPerformance).text())
                : await testGPU(renderer, gl);

            if (!prevPerformance) {
                await cache.put("/score", new Response(gpuPerformance));
                await cache.put("/timestamp", new Response(Date.now()));
            }

            if (gpuPerformance > MAX_ALLOWED_TIME_PER_FRAME_MS)
                quality = RenderQuality.LOW;
        } catch (e) {
            console.error(e);
            quality = RenderQuality.LOW;
        }
    } else quality = knownGPUS[foundGPU].allowedQuality;

    renderer.dispose();

    return Math.min(quality, RenderQuality.NORMAL);
}

export default checkGPU;
export { checkGPU };

function getUnmaskedInfo(gl) {
    const unMaskedInfo = { renderer: "", vendor: "" };
    const dbgRenderInfo = gl.getExtension("WEBGL_debug_renderer_info");

    if (dbgRenderInfo != null) {
        unMaskedInfo.renderer = gl.getParameter(dbgRenderInfo.UNMASKED_RENDERER_WEBGL);
        unMaskedInfo.vendor = gl.getParameter(dbgRenderInfo.UNMASKED_VENDOR_WEBGL);
    }

    return unMaskedInfo;
}

/**
 * 
 * @param {THREE.WebGLRenderer} renderer 
 * @param {WebGLRenderingContext} gl
 */
function testGPU(renderer, gl) {
    const geometry = new THREE.PlaneGeometry(2, 2, 1, 1);
    const material = new PerfTestMaterial(SHADER_ITERATION_COUNT);
    const camera = new THREE.Camera();
    const scene = new THREE.Scene();
    const mesh = new THREE.Mesh(geometry, material);
    const pixels = new Uint8Array(4);

    function dispose() {
        geometry.dispose();
        material.dispose();
    }

    function renderFrame() {
        return new Promise(resolve => {
            function doRender() {
                renderer.render(scene, camera);
                gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixels); // wait for gpu to render
                resolve();
            }

            requestAnimationFrame(doRender);
        });
    }

    scene.add(mesh);

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);

    const measurementStart = window.performance.now();
    let totalDuration = 0;

    const isTimeouted = () => (window.performance.now() - measurementStart) > TIME_UNTIL_TIMEOUT_MS;

    return new Promise(async resolve => {
        // Warm up
        for (let i = 0; i < GPU_GRACE_PERIOD && !isTimeouted(); i++) await renderFrame();

        // Run test
        for (let i = 0; i < GPU_TEST_ITERATIONS && !isTimeouted(); i++) {
            const begin = window.performance.now();

            await renderFrame();

            const finish = window.performance.now();
            const delta = finish - begin;

            totalDuration += delta;
        }

        const averateFrameTime = isTimeouted() ? Infinity : totalDuration / GPU_TEST_ITERATIONS;

        console.log(`Averate frame time: ${averateFrameTime.toFixed(4)}`);

        dispose();

        resolve(averateFrameTime);
    });
}